import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faUser,
  faCar,
  faIdCard,
  faMoneyBillWave,
  faCircleExclamation,
  faCheckCircle,
  faBan,
} from "@fortawesome/free-solid-svg-icons";
import { API_URL, API_URL_UPLOADS } from "./config"; // Ensure this is the correct import
import DashboardLayout from "../components/DashboardLayout/DasboardLayout";

function SearchPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [user, setUser] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [driver, setDriver] = useState(null);
  const [topUpList, setTopUpList] = useState([]);

  const fetchDriver = async (userId) => {
    try {
      const driverResult = await fetch(`${API_URL}/driver/${userId}`);
      if (!driverResult.ok) {
        throw new Error(`Error: ${driverResult.status}`);
      }
      const driverData = await driverResult.json();
      return driverData;
    } catch (error) {
      console.error("Failed to fetch driver data:", error);
      throw error;
    }
  };

  const fetchCustomer = async (userId) => {
    try {
      const customerResult = await fetch(
        `${API_URL}/customerdetails/${userId}`
      );
      if (!customerResult.ok) {
        throw new Error(`Error: ${customerResult.status}`);
      }
      const customerData = await customerResult.json();
      return customerData;
    } catch (error) {
      console.error("Failed to fetch customer data:", error);
      throw error;
    }
  };

  const fetchUserData = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/users/${userId}`);
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      throw error;
    }
  };

  const verifyUser = async (driverId, status) => {
    try {
      const result = await fetch(`${API_URL}/users/update_status/${driverId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status }),
      });

      await fetch(`${API_URL}/driver/update_status/${driverId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ membershipstatus: status }),
      });

      if (!result.ok) {
        throw new Error(`Error: ${result.status}`);
      }
    } catch (error) {
      console.error("Failed to update user status:", error);
    }
  };

  const fetchTopUp = async (driverId) => {
    try {
      const response = await fetch(`${API_URL}/topUp/topup/${driverId}`);
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("Failed to fetch top-up data:", error);
      return [];
    }
  };

  const suspend = async () => {
    let userId = user.userid;
    await verifyUser(userId, "Suspended");
    handleSearch(); // Refresh the search
  };
  const Pending = async () => {
    let userId = user.userid;
    await verifyUser(userId, "Pending Verification");
    handleSearch(); // Refresh the search
  };

  const Blacklist = async () => {
    let userId = user.userid;
    await verifyUser(userId, "Blacklisted");
    handleSearch(); // Refresh the search
  };

  const Activate = async () => {
    let userId = user.userid;
    await verifyUser(userId, "Verified");
    handleSearch(); // Refresh the search
  };

  const handleSearch = async (e) => {
    if (e) e.preventDefault();

    try {
      const userResults = await fetchUserData(searchTerm);
      if (userResults.length > 0) {
        const userData = userResults[0];
        setUser(userData);

        const customerData = await fetchCustomer(searchTerm);
        const driverData = await fetchDriver(searchTerm);
        const topUpResults = await fetchTopUp(userData.driver_id);

        setCustomer(customerData.length > 0 ? customerData[0] : null);
        setDriver(driverData.length > 0 ? driverData[0] : null);
        setTopUpList(topUpResults);
      } else {
        setUser(null);
        setCustomer(null);
        setDriver(null);
        setTopUpList([]);
      }
    } catch (error) {
      console.error("Search error:", error);
    }
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-white">
        {/* Header Section */}
        <div className="bg-white py-6 px-4 ">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-3xl font-bold text-[#002966] mb-6">
              User Search Dashboard
            </h1>
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="flex gap-2 bg-gradient-to-br from-[#ffc000] to-[#002966] rounded-lg p-2 shadow-lg">
                <input
                  type="text"
                  name="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Enter user ID to search..."
                  className="flex-1 px-4 py-2 outline-none text-[#002966] text-lg"
                />
                <button
                  type="submit"
                  className="bg-[#002966] hover:bg-[#e6ac00] px-6 py-2 rounded-lg transition-colors duration-200"
                >
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    className="text-[#ffc000]"
                  />
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Status Bar - Only shown when user is found */}
          {user && (
            <div className="mb-8 bg-white rounded-lg shadow-lg p-4 border-l-4 border-[#002966]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div
                    className={`text-lg font-semibold ${
                      user.status === "Verified"
                        ? "text-green-600"
                        : user.status === "Suspended"
                        ? "text-orange-600"
                        : "text-red-600"
                    }`}
                  >
                    <FontAwesomeIcon
                      icon={
                        user.status === "Verified"
                          ? faCheckCircle
                          : user.status === "Suspended"
                          ? faCircleExclamation
                          : faBan
                      }
                      className="mr-2"
                    />
                    Status: {user.status}
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={Activate}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                  >
                    Activate
                  </button>
                  <button
                    onClick={Pending}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-orange-700 transition-colors"
                  >
                    UnVerify
                  </button>
                  <button
                    onClick={suspend}
                    className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors"
                  >
                    Suspend
                  </button>

                  <button
                    onClick={Blacklist}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  >
                    Blacklist
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Grid Layout for Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* User Information Card */}
            {user && (
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="bg-[#002966] px-6 py-4 flex items-center">
                  <FontAwesomeIcon
                    icon={faUser}
                    className="text-[#ffc000] mr-3"
                  />
                  <h2 className="text-xl font-semibold text-white">
                    User Information
                  </h2>
                </div>
                <div className="p-6 space-y-4">
                  {Object.entries(user).map(
                    ([key, value]) =>
                      key !== "password" && (
                        <div
                          key={key}
                          className="flex border-b border-gray-200 py-2"
                        >
                          <span className="font-medium text-[#002966] w-1/3 capitalize">
                            {key.replace(/_/g, " ")}:
                          </span>
                          <span className="text-gray-700 w-2/3">
                            {value || "N/A"}
                          </span>
                        </div>
                      )
                  )}
                </div>
              </div>
            )}

            {/* Driver Information Card */}
            {driver && (
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="bg-[#002966] px-6 py-4 flex items-center">
                  <FontAwesomeIcon
                    icon={faCar}
                    className="text-[#ffc000] mr-3"
                  />
                  <h2 className="text-xl font-semibold text-white">
                    Driver Information
                  </h2>
                </div>
                <div className="p-6">
                  {/* Images Section */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {[
                      "profilePic",
                      "driver_license_image",
                      "id_image",
                      "vehicle_license_image",
                    ].map(
                      (imgKey) =>
                        driver[imgKey] && (
                          <div key={imgKey} className="relative group">
                            <img
                              src={`${API_URL_UPLOADS}/${driver[imgKey].replace(
                                /\\/g,
                                "/"
                              )}`}
                              alt={imgKey.replace(/_/g, " ")}
                              className="w-full h-48 object-cover rounded-lg shadow-md transition-transform duration-200 group-hover:scale-105"
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 text-sm rounded-b-lg">
                              {imgKey
                                .replace(/_/g, " ")
                                .replace(/Pic/g, " Picture")}
                            </div>
                          </div>
                        )
                    )}
                  </div>

                  {/* Driver Details */}
                  <div className="space-y-4">
                    {Object.entries(driver).map(
                      ([key, value]) =>
                        ![
                          "profilePic",
                          "driver_license_image",
                          "id_image",
                          "vehicle_license_image",
                          "password",
                        ].includes(key) && (
                          <div
                            key={key}
                            className="flex border-b border-gray-200 py-2"
                          >
                            <span className="font-medium text-[#002966] w-1/3 capitalize">
                              {key.replace(/_/g, " ")}:
                            </span>
                            <span className="text-gray-700 w-2/3">
                              {value || "N/A"}
                            </span>
                          </div>
                        )
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Customer Information Card */}
            {customer && (
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="bg-[#002966] px-6 py-4 flex items-center">
                  <FontAwesomeIcon
                    icon={faIdCard}
                    className="text-[#ffc000] mr-3"
                  />
                  <h2 className="text-xl font-semibold text-white">
                    Customer Information
                  </h2>
                </div>
                <div className="p-6">
                  {customer.profilePic && (
                    <div className="mb-6">
                      <img
                        src={`${API_URL_UPLOADS}/${customer.profilePic.replace(
                          /\\/g,
                          "/"
                        )}`}
                        alt="Profile"
                        className="w-32 h-32 rounded-full mx-auto shadow-lg object-cover"
                      />
                    </div>
                  )}
                  <div className="space-y-4">
                    {Object.entries(customer).map(
                      ([key, value]) =>
                        key !== "profilePic" && (
                          <div
                            key={key}
                            className="flex border-b border-gray-200 py-2"
                          >
                            <span className="font-medium text-[#002966] w-1/3 capitalize">
                              {key.replace(/_/g, " ")}:
                            </span>
                            <span className="text-gray-700 w-2/3">
                              {value || "N/A"}
                            </span>
                          </div>
                        )
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* Top-Up Information Card */}
          {topUpList.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-[#002966] px-6 py-4 flex items-center">
                <FontAwesomeIcon
                  icon={faMoneyBillWave}
                  className="text-[#ffc000] mr-3"
                />
                <h2 className="text-xl font-semibold text-white">
                  Top-Up History
                </h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {topUpList.map((topUp, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-[#002966]">
                          Amount: {topUp.amount} {topUp.currency}
                        </span>
                        <span className="text-gray-600 text-sm">
                          {new Date(topUp.date).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-gray-600 mt-2">
                        {topUp.description || "No description available"}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* No Results Message */}
          {user === null && (
            <div className="text-center py-12">
              <div className="text-gray-400 text-xl">No results found</div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default SearchPage;
